/**
 * Le planning lu depuis l'INTERVENTION UNIQUE (lot U1c, ADR-0016) : tout ce qui se décide sans navigateur.
 *
 * Une case de la grille = un jour travaillé d'une intervention pour un membre de son équipe ; un jour sans équipe tombe dans
 * la ligne « À affecter » à sa date. Le serveur calcule zone, retard et urgence : rien n'est recalculé ici. Aucun prix.
 */
import type { AjustementHeures, BonResume, BriefingIntervention, CaseSemaine, CreneauJour, EntreeIntervention, InterventionPlanning, LignePrevue, RaisonSansLigne } from '../api/client';
import { LIBELLE_ETAT, LIBELLE_SUITE, classeEtat } from './interventions';
import { dureeFr, jourFr, jourLong, jourValide, lireDuree, type FormulaireVisite } from './planning';
import { debutDePeriode, type VuePlanning } from './planning-calendrier';

/** Les cases d'une ligne de la grille (un technicien, ou `null` = « À affecter ») pour un jour, dans l'ordre reçu. */
export function casesDeLaLigne(cases: readonly CaseSemaine[], technicienId: string | null, jour: string): CaseSemaine[] {
  return cases.filter((c) => c.day === jour && c.technicianId === technicienId);
}

/** « BI-2026-00012 », ou « prévue » tant qu'une occurrence de tournée n'a pas de numéro. */
export function numeroAffiche(i: Pick<InterventionPlanning, 'number'>): string {
  return i.number ?? 'prévue';
}

/** Le numéro raccourci d'un bloc : « 00016 » pour « BI-2026-00016 » (le numéro entier est dans l'infobulle). */
export function numeroCourt(numero: string): string {
  const fin = numero.split('-').pop() ?? '';
  return fin === '' ? numero : fin;
}

/** Une recherche du planning ne part qu'à partir de 2 caractères utiles. */
export const RECHERCHE_MIN = 2;

export function rechercheUtile(texte: string): string | null {
  const q = texte.trim();
  return q.length >= RECHERCHE_MIN ? q : null;
}

/** Où retrouver une intervention trouvée : son premier jour prévu, sinon le jour du bon ; `null` = sans jour (liste « À affecter »). */
export function jourDuResultat(b: Pick<BonResume, 'premierJour' | 'performedOn'>): string | null {
  return b.premierJour ?? b.performedOn ?? null;
}

/**
 * Ce que l'adresse du planning devient quand on choisit un résultat : l'intervention sélectionnée, la semaine de son jour
 * (aucune semaine sans jour : elle est dans « À affecter »), et les annulées affichées quand elle est annulée (sinon
 * elle resterait invisible). Une clé absente garde la valeur actuelle de l'adresse.
 */
export function adresseVersResultat(b: Pick<BonResume, 'id' | 'status' | 'premierJour' | 'performedOn'>, vue: VuePlanning = 'semaine'): Record<string, string> {
  const jour = jourDuResultat(b);
  return {
    intervention: b.id,
    // La période qui contient le jour, dans la vue affichée : sa semaine (le lundi) ou son mois (le 1er).
    ...(jour === null ? {} : { semaine: debutDePeriode(vue, jour) }),
    ...(b.status === 'CANCELLED' ? { annulees: 'oui' } : {}),
  };
}

/** La ligne d'un résultat de recherche : numéro, machine, client, et où la trouver. */
export function ligneResultat(b: Pick<BonResume, 'number' | 'asset' | 'customer' | 'site' | 'status' | 'premierJour' | 'performedOn'>): {
  numero: string;
  machine: string;
  client: string;
  quand: string;
  etat: string;
} {
  const jour = jourDuResultat(b);
  return {
    numero: b.number,
    machine: b.asset?.label ?? (b.asset === null ? 'Sans machine' : 'Machine'),
    client: [b.customer?.name, b.site?.name].filter(Boolean).join(' · '),
    quand: jour === null ? 'sans jour : liste « À affecter »' : jourFr(jour),
    etat: LIBELLE_ETAT[b.status],
  };
}

/** La machine d'une intervention : son nom, « Machine à identifier » chez un client sans machine, « Atelier » pour l'atelier. */
export function libelleMachine(i: Pick<InterventionPlanning, 'kind' | 'asset'>): string {
  if (i.kind === 'WORKSHOP') return 'Atelier';
  if (i.asset === null) return 'Machine à identifier';
  return i.asset.label ?? 'Machine';
}

/** Le titre d'un bloc : le client, ou le titre de l'atelier. */
export function titreIntervention(i: Pick<InterventionPlanning, 'kind' | 'title' | 'customerName'>): string {
  if (i.kind === 'WORKSHOP') return i.title ?? 'Atelier';
  return i.customerName ?? 'Client inconnu';
}

/** Les lignes d'un bloc de la grille. */
export function lignesDuBloc(c: CaseSemaine): { duree: string; titre: string; lieu: string; machine: string } {
  return {
    duree: c.minutesPlanned === null ? 'durée à préciser' : dureeFr(c.minutesPlanned),
    titre: titreIntervention(c.intervention),
    lieu: c.intervention.site?.name ?? '',
    machine: libelleMachine(c.intervention),
  };
}

/** L'infobulle d'un bloc : tout ce que le bloc n'a pas la place de dire. */
export function infobulleBloc(c: CaseSemaine): string {
  const i = c.intervention;
  const parties = [
    `${numeroAffiche(i)} — ${titreIntervention(i)}`,
    i.site ? `Lieu : ${i.site.name}` : null,
    `Machine : ${libelleMachine(i)}`,
    i.natureLabel ? `Nature : ${i.natureLabel}` : null,
    c.minutesPlanned === null ? null : `Durée prévue : ${dureeFr(c.minutesPlanned)}`,
    c.isLead ? 'Chef du jour' : null,
    i.tentative ? 'À confirmer' : null,
  ];
  return parties.filter((p): p is string => p !== null).join('\n');
}

/** Le temps prévu d'un technicien un jour : la somme des durées connues, et combien de cases n'en ont pas. */
export function tempsPrevuDuJour(cases: readonly CaseSemaine[], technicienId: string, jour: string): { minutes: number; sansDuree: number } {
  let minutes = 0;
  let sansDuree = 0;
  for (const c of casesDeLaLigne(cases, technicienId, jour)) {
    if (c.minutesPlanned === null) sansDuree += 1;
    else minutes += c.minutesPlanned;
  }
  return { minutes, sansDuree };
}

/** Pourquoi il n'y a pas de pièces à prévoir, en français et avec le geste qui règle le manque. */
export const PHRASE_RAISON: Readonly<Record<RaisonSansLigne, string>> = {
  SANS_MACHINE: 'Machine à identifier : aucune pièce à prévoir tant qu’elle n’est pas connue.',
  SANS_GAMME: 'Cette machine n’a pas de gamme d’entretien rattachée.',
  GAMME_SANS_VERSION: 'La gamme de cette machine n’a aucune version en vigueur.',
  PALIER_A_CHOISIR: 'Aucun palier n’est prévu : choisissez-le pour voir les pièces.',
  PALIER_INCONNU: 'Le palier prévu n’existe plus dans la gamme : choisissez-en un autre.',
  AUCUNE_PIECE: 'Ce palier n’a aucune pièce à remplacer.',
};

const LIBELLE_GENRE: Readonly<Record<LignePrevue['kind'], string>> = {
  PART: 'Pièce',
  LABOR: 'Main d’œuvre',
  TRAVEL: 'Déplacement',
  FLAT_FEE: 'Forfait',
  EQUIPMENT: 'Matériel',
  ACCESSORY: 'Accessoire',
};

export function libelleGenre(kind: LignePrevue['kind']): string {
  return LIBELLE_GENRE[kind];
}

/** D'où viennent les lignes prévues, dit à l'écran. */
export function phraseSourceLignes(b: Pick<BriefingIntervention, 'lignesPrevues' | 'gamme'>): string {
  const { source, raison } = b.lignesPrevues;
  if (source === 'SAISIES') return 'Lignes enregistrées sur l’intervention.';
  if (source === 'GAMME') {
    const g = b.gamme;
    const palier = g?.palier ? `palier ${g.palier.code}` : 'palier prévu';
    return `Pièces du ${palier} de la gamme « ${g?.gamme?.label ?? ''} », lues à l’ouverture : rien n’est recopié.`;
  }
  return raison === null ? 'Rien à prévoir.' : PHRASE_RAISON[raison];
}

// --- L'adresse de la page ---------------------------------------------------------------------------

export interface ParametresPlanningInterventions {
  readonly semaine: string | null;
  readonly intervention: string | null;
  /** Montrer aussi les interventions annulées (case « Afficher les annulées »). */
  readonly annulees: boolean;
  /** La période affichée : la semaine (par défaut) ou le mois civil (`vue=mois`). */
  readonly vue: VuePlanning;
}

/** Lit l'adresse : un jour mal écrit est ignoré (jamais une page cassée par un lien tronqué). */
export function lireParametresInterventions(params: URLSearchParams): ParametresPlanningInterventions {
  const semaine = params.get('semaine');
  return {
    semaine: semaine !== null && jourValide(semaine) ? semaine : null,
    intervention: params.get('intervention'),
    annulees: params.get('annulees') === 'oui',
    vue: params.get('vue') === 'mois' ? 'mois' : 'semaine',
  };
}

/**
 * La couleur d'une intervention selon OÙ ELLE EN EST (comme au planning de Praxedo) : À affecter (gris), Planifiée (jaune),
 * En cours (vert), Réalisée (bleu : le bon est soumis, il attend son contrôle), Validée (indigo : le bon est figé), Annulée
 * (rouge). « Envoyée » (orange, l'envoi au téléphone) existe au cycle de vie mais personne ne l'atteint avant l'application du
 * technicien : elle a sa couleur, pas de ligne dans la légende.
 */
export type CouleurEtat = 'a-affecter' | 'planifiee' | 'envoyee' | 'en-cours' | 'realisee' | 'validee' | 'annulee';

export interface EtatCouleur {
  readonly classe: CouleurEtat;
  readonly libelle: string;
}

const ETATS_COULEUR: Readonly<Record<CouleurEtat, EtatCouleur>> = {
  'a-affecter': { classe: 'a-affecter', libelle: 'À affecter' },
  planifiee: { classe: 'planifiee', libelle: 'Planifiée' },
  envoyee: { classe: 'envoyee', libelle: 'Envoyée' },
  'en-cours': { classe: 'en-cours', libelle: 'En cours' },
  realisee: { classe: 'realisee', libelle: 'Réalisée, à valider' },
  validee: { classe: 'validee', libelle: 'Validée' },
  annulee: { classe: 'annulee', libelle: 'Annulée' },
};

/** Les états de la légende, dans l'ordre du cycle de vie. */
export const LEGENDE_ETATS: readonly EtatCouleur[] = [
  ETATS_COULEUR['a-affecter'],
  ETATS_COULEUR.planifiee,
  ETATS_COULEUR['en-cours'],
  ETATS_COULEUR.realisee,
  ETATS_COULEUR.validee,
  ETATS_COULEUR.annulee,
];

/** L'état d'une intervention, en couleur ; un état inconnu (serveur plus récent) prend le gris « à affecter ». */
export function couleurEtat(status: string): EtatCouleur {
  switch (status) {
    case 'PLANNED':
      return ETATS_COULEUR.planifiee;
    case 'DISPATCHED':
      return ETATS_COULEUR.envoyee;
    case 'IN_PROGRESS':
      return ETATS_COULEUR['en-cours'];
    case 'COMPLETED_BY_TECH':
    case 'TO_REVIEW':
      return ETATS_COULEUR.realisee;
    case 'REVIEWED':
    case 'TRANSMITTED':
    case 'CLASSIFIED':
    case 'TO_QUOTE':
    case 'TO_INVOICE':
    case 'CLOSED':
      return ETATS_COULEUR.validee;
    case 'CANCELLED':
      return ETATS_COULEUR.annulee;
    default:
      return ETATS_COULEUR['a-affecter'];
  }
}

/** « 2 bons validés sur 3 » : l'avancement d'un passage groupé ; jamais de division par zéro. */
export function phraseBonsValides(valides: number, total: number): string {
  if (total === 0) return 'Aucune intervention dans ce passage.';
  if (valides === 0) return `Aucun bon validé sur ${total}`;
  return `${valides} ${valides > 1 ? 'bons validés' : 'bon validé'} sur ${total}`;
}

/** La part du travail faite, en pourcentage entier, pour la barre ; jamais plus de 100, jamais de division par zéro. */
export function partValidee(valides: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(100, Math.round((valides / total) * 100));
}

/** L'état d'une intervention du passage, dit en français ; un état inconnu (serveur plus récent) se dit tel quel. */
export function etatDuBon(status: string): { readonly libelle: string; readonly classe: string } {
  const connu = (LIBELLE_ETAT as Readonly<Record<string, string>>)[status];
  return { libelle: connu ?? status, classe: connu === undefined ? 'suspendu' : classeEtat(status as keyof typeof LIBELLE_ETAT) };
}

/** Le type d'une suite à donner, en français ; un type inconnu se dit tel quel. */
export function libelleSuite(kind: string): string {
  return (LIBELLE_SUITE as Readonly<Record<string, string>>)[kind] ?? kind;
}

/** Le lien d'une intervention dans ce planning : sa semaine, et elle-même (le briefing s'ouvre). */
export function lienIntervention(id: string, jour: string | null): string {
  const p = new URLSearchParams();
  if (jour !== null) p.set('semaine', debutDePeriode('semaine', jour));
  p.set('intervention', id);
  return `/planning/interventions?${p.toString()}`;
}

// --- Créer une intervention depuis le volet (même formulaire que la visite) ---------------------------------------

/**
 * Le corps de la création d'une INTERVENTION d'après le formulaire du volet. Une date donne UN jour travaillé ; sans
 * technicien (« date fixe ») le jour tombe dans la ligne « À affecter » ; une fenêtre sans date attend dans la liste de droite.
 * `cle` rend la création rejouable : un double clic ne crée pas deux interventions.
 */
export function versEntreeIntervention(f: FormulaireVisite, cle: string): EntreeIntervention {
  const duree = lireDuree(f.dureeTexte);
  const description = f.description.trim();
  const commande = f.commande.trim();
  return {
    kind: f.genre,
    clientKey: cle,
    ...(f.genre === 'WORKSHOP'
      ? { title: f.titre.trim() }
      : {
          natureId: f.natureId,
          ...(f.machines.length === 0 && f.siteId !== null ? { siteId: f.siteId } : {}),
          machines: f.machines.map((x) => ({ assetId: x.assetId, ...(x.levelKey ? { levelKey: x.levelKey } : {}) })),
        }),
    ...(f.mode === 'FENETRE' ? { windowStart: f.fenetreDebut, windowEnd: f.fenetreFin } : { jours: [f.date] }),
    technicianIds: f.mode === 'DATE_TECHNICIEN' ? [...f.technicienIds] : [],
    ...(duree !== null && duree !== 'ERREUR' ? { dureeMinutes: duree } : {}),
    ...(description !== '' ? { description } : {}),
    ...(commande !== '' ? { customerOrderRef: commande } : {}),
    ...(f.tentative ? { tentative: true } : {}),
  };
}

// --- Déplacer un bloc (glisser ou menu) : la suite d'écritures qui l'accomplit ----------------------------------------

export type EtapeDeplacement =
  | { readonly type: 'AJOUTER_JOUR'; readonly jour: string }
  | { readonly type: 'RETIRER_JOUR'; readonly jour: string }
  | { readonly type: 'AJOUTER_MEMBRE'; readonly jour: string; readonly technicianId: string }
  | { readonly type: 'RETIRER_MEMBRE'; readonly jour: string; readonly technicianId: string }
  | { readonly type: 'DEFINIR_CHEF'; readonly jour: string; readonly technicianId: string }
  | { readonly type: 'DEFINIR_CRENEAU'; readonly jour: string; readonly slot: CreneauJour };

export interface CibleCase {
  readonly jour: string;
  /** Nul = la ligne « À affecter ». */
  readonly technicianId: string | null;
  /** La demi-journée où l'on dépose (lot T0) ; absente = le créneau ne change pas. */
  readonly slot?: CreneauJour;
}

/** L'équipe d'un jour d'une intervention d'après les cases reçues : le chef d'abord, puis les autres dans l'ordre du serveur. */
export function equipeDuJour(cases: readonly CaseSemaine[], interventionId: string, jour: string): string[] {
  const membres = cases.filter((c) => c.intervention.id === interventionId && c.day === jour && c.technicianId !== null);
  const chef = membres.filter((c) => c.isLead).map((c) => c.technicianId as string);
  const autres = membres.filter((c) => !c.isLead).map((c) => c.technicianId as string);
  return [...chef, ...autres];
}

/** Une intervention peut-elle être déplacée ? Seule une intervention ouverte (ni validée, ni annulée) se déplace. */
export function peutDeplacer(i: Pick<InterventionPlanning, 'status'>, peutGerer: boolean): boolean {
  return peutGerer && ['TO_ASSIGN', 'PLANNED', 'DRAFT', 'IN_PROGRESS'].includes(i.status);
}

/**
 * Une intervention se confirme (se numérote) quand elle n'a pas encore de numéro — une occurrence de tournée — qu'elle est
 * ouverte et que c'est une intervention chez un client : une intervention d'atelier n'est pas un bon, elle n'a pas de numéro.
 */
export function peutConfirmer(i: Pick<InterventionPlanning, 'status' | 'number' | 'kind'>, peutGerer: boolean): boolean {
  return i.number === null && i.kind === 'CUSTOMER' && peutDeplacer(i, peutGerer);
}

/**
 * Le geste du planning vers le bon d'une intervention (ADR-0016 : planning et bon sont UN objet). « Saisir le bon » ouvre l'éditeur
 * du bon sur CETTE intervention (`?saisie=1`) tant qu'elle est à faire ; « Ouvrir le bon » mène au bon soumis ou validé (relire,
 * valider, rouvrir pour corriger). Rien pour une intervention d'atelier (pas un bon), annulée, ou sans machine (il faut d'abord
 * l'identifier : la machine d'un bon ne se choisit pas au vol). Saisir exige le droit de saisir les bons ; lire un bon, non.
 */
export function lienDuBon(
  i: Pick<InterventionPlanning, 'id' | 'status' | 'kind' | 'asset'>,
  peutSaisirBons: boolean,
): { readonly href: string; readonly libelle: string; readonly principal: boolean } | null {
  if (i.kind !== 'CUSTOMER') return null;
  if (i.status === 'TO_REVIEW' || i.status === 'REVIEWED') {
    return { href: `/interventions?bon=${i.id}`, libelle: 'Ouvrir le bon', principal: false };
  }
  if (['TO_ASSIGN', 'PLANNED', 'IN_PROGRESS', 'DRAFT'].includes(i.status) && peutSaisirBons && i.asset !== null) {
    return { href: `/interventions?bon=${i.id}&saisie=1`, libelle: 'Saisir le bon', principal: true };
  }
  return null;
}

/** Ce que dit l'écran après la confirmation : le numéro est là pour de bon (il ne se réutilise jamais). */
export function phraseConfirmation(numero: string): string {
  return `Intervention confirmée : elle porte maintenant le numéro ${numero}.`;
}

/**
 * Les écritures qui amènent le bloc `prise` sur la case `cible`, ou le refus en français. Chaque étape est une route
 * incrémentale qui ne dépend pas de la semaine affichée (une intervention peut durer des mois). Ajouter AVANT retirer : un échec
 * au milieu laisse l'intervention avec trop de monde, jamais avec un jour vide.
 *  - même jour, autre ligne : le technicien d'origine est remplacé (les autres restent), « À affecter » = il part ;
 *  - autre jour : tout le jour part avec son équipe, le technicien d'origine remplacé par celui de la case.
 */
export function etapesDeDeplacement(
  cases: readonly CaseSemaine[],
  prise: CaseSemaine,
  cible: CibleCase,
): { readonly etapes: readonly EtapeDeplacement[] } | { readonly refus: string } {
  const id = prise.intervention.id;
  const origineJour = prise.day;
  const equipe = equipeDuJour(cases, id, origineJour);
  const depuis = prise.technicianId;
  // Le créneau voulu : posé quand on dépose sur une demi-journée qui n'est pas déjà la sienne (un jour neuf n'a pas de créneau).
  const creneau = (jour: string, actuel: CreneauJour | null): EtapeDeplacement[] =>
    cible.slot === undefined || cible.slot === actuel ? [] : [{ type: 'DEFINIR_CRENEAU', jour, slot: cible.slot }];
  if (cible.jour === origineJour && cible.technicianId === depuis) return { etapes: creneau(origineJour, prise.slot) };

  // L'équipe voulue : le technicien d'origine remplacé par celui de la case (ou parti, sur « À affecter »).
  let voulue: string[];
  if (depuis !== null) {
    voulue = equipe.map((t) => (t === depuis ? cible.technicianId : t)).filter((t): t is string => t !== null);
  } else {
    voulue = cible.technicianId === null ? [...equipe] : [...equipe, cible.technicianId];
  }
  if (new Set(voulue).size !== voulue.length) return { refus: 'Ce technicien fait déjà partie de l’équipe de ce jour.' };

  if (cible.jour !== origineJour) {
    const dejaCeJour = cases.some((c) => c.intervention.id === id && c.day === cible.jour);
    if (dejaCeJour) return { refus: 'Cette intervention travaille déjà ce jour-là.' };
    return {
      etapes: [
        { type: 'AJOUTER_JOUR', jour: cible.jour },
        ...voulue.map((technicianId): EtapeDeplacement => ({ type: 'AJOUTER_MEMBRE', jour: cible.jour, technicianId })),
        { type: 'RETIRER_JOUR', jour: origineJour },
        ...creneau(cible.jour, null),
      ],
    };
  }

  const etapes: EtapeDeplacement[] = [];
  const ajoutes = voulue.filter((t) => !equipe.includes(t));
  const retires = equipe.filter((t) => !voulue.includes(t));
  for (const technicianId of ajoutes) etapes.push({ type: 'AJOUTER_MEMBRE', jour: origineJour, technicianId });
  const chefActuel = equipe[0];
  const chefVoulu = voulue[0];
  if (chefVoulu !== undefined && chefVoulu !== chefActuel && retires.includes(chefActuel as string)) {
    etapes.push({ type: 'DEFINIR_CHEF', jour: origineJour, technicianId: chefVoulu });
  }
  for (const technicianId of retires) etapes.push({ type: 'RETIRER_MEMBRE', jour: origineJour, technicianId });
  etapes.push(...creneau(origineJour, prise.slot));
  return { etapes };
}

/** « Matin », « Après-midi », « Dans la journée » : le libellé d'un créneau. */
export const LIBELLE_CRENEAU: Readonly<Record<CreneauJour, string>> = { MORNING: 'Matin', AFTERNOON: 'Après-midi' };

/** « RDV 8 h 00 » : l'heure de rendez-vous d'une case, à la française ; vide sans heure. */
export function rendezVousFr(heure: string | null): string {
  if (heure === null) return '';
  const [h, m] = heure.split(':');
  return `RDV ${Number(h)} h ${m ?? '00'}`;
}

/**
 * Le choix du formulaire de créneau (`slot` : '' / MORNING / AFTERNOON ; `heure` : '' ou « HH:MM ») lu en valeurs à envoyer :
 * '' devient `null` (« dans la journée », pas d'heure). Une heure mal écrite est refusée avec la raison.
 */
export function lireChoixCreneau(choix: { readonly slot: string; readonly heure: string }):
  | { readonly ok: true; readonly valeur: { readonly slot: CreneauJour | null; readonly appointmentTime: string | null } }
  | { readonly ok: false; readonly erreur: string } {
  const slot = choix.slot === 'MORNING' || choix.slot === 'AFTERNOON' ? choix.slot : null;
  if (choix.slot !== '' && slot === null) return { ok: false, erreur: 'Créneau inconnu.' };
  const heure = choix.heure.trim();
  if (heure === '') return { ok: true, valeur: { slot, appointmentTime: null } };
  if (!/^([01]\d|2[0-3]):([0-5]\d)$/.test(heure)) return { ok: false, erreur: 'L’heure de rendez-vous s’écrit HH:MM (par exemple 08:00).' };
  return { ok: true, valeur: { slot, appointmentTime: heure } };
}

/** Le choix diffère-t-il de ce qui est enregistré ? (rien à envoyer sinon) */
export function choixCreneauModifie(
  choix: { readonly slot: CreneauJour | null; readonly appointmentTime: string | null },
  enregistre: { readonly slot: CreneauJour | null; readonly appointmentTime: string | null },
): boolean {
  return choix.slot !== enregistre.slot || choix.appointmentTime !== enregistre.appointmentTime;
}

/** Ce que le bloc dit de sa place dans la journée : « Matin · RDV 8 h 00 », « Après-midi », ou rien. */
export function placeDuBloc(c: Pick<CaseSemaine, 'slot' | 'appointmentTime'>): string {
  return [c.slot === null ? '' : LIBELLE_CRENEAU[c.slot], rendezVousFr(c.appointmentTime)].filter((x) => x !== '').join(' · ');
}

/**
 * Monter (-1) ou descendre (+1) une intervention dans la case d'un technicien un jour : la liste COMPLÈTE de la case dans le
 * nouvel ordre (pour la route de rangement), ou la raison du refus. On n'échange qu'avec la voisine du MÊME créneau et de la MÊME
 * heure de rendez-vous : sinon c'est le créneau ou l'heure qui décide de l'ordre, pas le rang.
 */
export function rangementDansLaCase(
  casesDeLaCase: readonly CaseSemaine[],
  interventionId: string,
  sens: -1 | 1,
): { readonly items: readonly { readonly interventionId: string; readonly revision: number }[] } | { readonly refus: string } {
  const i = casesDeLaCase.findIndex((c) => c.intervention.id === interventionId);
  const voisine = casesDeLaCase[i + sens];
  const ici = casesDeLaCase[i];
  if (ici === undefined || voisine === undefined) return { refus: sens < 0 ? 'Elle est déjà la première de la journée.' : 'Elle est déjà la dernière de la journée.' };
  if (voisine.slot !== ici.slot) return { refus: 'Changez plutôt son créneau (matin ou après-midi).' };
  if (voisine.appointmentTime !== ici.appointmentTime) return { refus: 'L’heure de rendez-vous décide de l’ordre : changez l’heure.' };
  const ordre = [...casesDeLaCase];
  ordre[i] = voisine;
  ordre[i + sens] = ici;
  return { items: ordre.map((c) => ({ interventionId: c.intervention.id, revision: c.intervention.revision })) };
}

/** La phrase du bilan d'un déplacement réussi. */
export function phraseDeplacementIntervention(cible: CibleCase, nomTechnicien: string | null): string {
  const quand = jourLongPhrase(cible.jour) + (cible.slot === undefined ? '' : cible.slot === 'MORNING' ? ' (matin)' : ' (après-midi)');
  return cible.technicianId === null ? `Déplacée au ${quand}, équipe à choisir.` : `Déplacée au ${quand}, avec ${nomTechnicien ?? 'le technicien choisi'}.`;
}

function jourLongPhrase(jour: string): string {
  return jourLong(jour);
}

/**
 * Une intervention SANS jour (la liste de droite) tombe sur une case : le jour est ajouté, et le technicien de la ligne entre
 * dans l'équipe (« À affecter » : le jour attend son équipe).
 */
export function etapesDepuisListe(cible: CibleCase): readonly EtapeDeplacement[] {
  return [
    { type: 'AJOUTER_JOUR', jour: cible.jour },
    ...(cible.technicianId === null ? [] : [{ type: 'AJOUTER_MEMBRE', jour: cible.jour, technicianId: cible.technicianId } as const]),
    ...(cible.slot === undefined ? [] : [{ type: 'DEFINIR_CRENEAU', jour: cible.jour, slot: cible.slot } as const]),
  ];
}

/** Les écritures dont `executerEtapes` a besoin : chacune rend la révision NOUVELLE de l'intervention. */
export interface AppelsEquipe {
  readonly ajouterJour: (id: string, revision: number, jour: string) => Promise<{ readonly revision: number }>;
  readonly retirerJour: (id: string, revision: number, jour: string) => Promise<{ readonly revision: number }>;
  readonly ajouterMembre: (id: string, revision: number, jour: string, technicianId: string) => Promise<{ readonly revision: number }>;
  readonly retirerMembre: (id: string, revision: number, jour: string, technicianId: string) => Promise<{ readonly revision: number }>;
  readonly definirChef: (id: string, revision: number, jour: string, technicianId: string) => Promise<{ readonly revision: number }>;
  readonly definirCreneau: (id: string, revision: number, jour: string, slot: CreneauJour) => Promise<{ readonly revision: number }>;
}

/**
 * Joue les étapes l'une après l'autre en reprenant à chaque fois la révision rendue par la précédente. Au premier refus (409 d'une
 * modification concurrente, par exemple) elle s'arrête : l'erreur remonte, les étapes déjà faites restent faites (la page relit).
 */
export async function executerEtapes(id: string, revisionDepart: number, etapes: readonly EtapeDeplacement[], appels: AppelsEquipe): Promise<number> {
  let revision = revisionDepart;
  for (const e of etapes) {
    const r =
      e.type === 'AJOUTER_JOUR'
        ? await appels.ajouterJour(id, revision, e.jour)
        : e.type === 'RETIRER_JOUR'
          ? await appels.retirerJour(id, revision, e.jour)
          : e.type === 'AJOUTER_MEMBRE'
            ? await appels.ajouterMembre(id, revision, e.jour, e.technicianId)
            : e.type === 'RETIRER_MEMBRE'
              ? await appels.retirerMembre(id, revision, e.jour, e.technicianId)
              : e.type === 'DEFINIR_CRENEAU'
                ? await appels.definirCreneau(id, revision, e.jour, e.slot)
                : await appels.definirChef(id, revision, e.jour, e.technicianId);
    revision = r.revision;
  }
  return revision;
}

/** « mardi 3 novembre », « Entre le 1 et le 30 novembre », « En retard de 3 j » : quand une intervention de la carte d'une machine a lieu. */
export function phraseQuandIntervention(
  i: { readonly prochainJour: string | null; readonly windowStart: string | null; readonly windowEnd: string | null },
  _aujourdhui: string,
): string {
  if (i.prochainJour !== null) return jourLong(i.prochainJour);
  if (i.windowStart !== null && i.windowEnd !== null) return `Entre le ${jourFr(i.windowStart)} et le ${jourFr(i.windowEnd)}`;
  return 'Sans date';
}

/** L'équipe du prochain jour : les noms, ou « équipe à choisir ». */
export function phraseEquipeDuJour(techniciens: readonly { readonly name: string }[]): string {
  return techniciens.length === 0 ? 'équipe à choisir' : techniciens.map((t) => t.name).join(', ');
}

// --- Suite à donner → intervention ---------------------------------------------------------------------------

const GENRES_A_PLANIFIER: ReadonlySet<string> = new Set(['RETURN_TO_PLAN', 'REVISION_TO_PLAN']);

/**
 * « Convertir en intervention » : permis pour un retour ou une révision à planifier encore ouverts et sans intervention
 * OUVERTE qui la traite déjà. Sinon le bouton est grisé AVEC sa raison (jamais un bouton qui disparaît sans explication).
 */
export function activationConvertirIntervention(
  suite: {
    readonly kind: string;
    readonly status: string;
    readonly treatedBy: { readonly id: string; readonly number: string; readonly status: string } | null;
  },
  peutGerer: boolean,
): { readonly visible: boolean; readonly actif: boolean; readonly raison: string | null } {
  if (!GENRES_A_PLANIFIER.has(suite.kind)) return { visible: false, actif: false, raison: null };
  if (suite.status !== 'OPEN') return { visible: true, actif: false, raison: 'Cette suite est déjà réglée.' };
  const ouverte = suite.treatedBy !== null && ['TO_ASSIGN', 'PLANNED', 'IN_PROGRESS', 'DRAFT', 'TO_REVIEW'].includes(suite.treatedBy.status);
  if (ouverte && suite.treatedBy !== null) {
    return { visible: true, actif: false, raison: `L'intervention ${suite.treatedBy.number} traite déjà cette suite : ouvrez-la ou annulez-la avant d'en créer une autre.` };
  }
  if (!peutGerer) return { visible: true, actif: false, raison: 'Il faut le droit de gérer le planning pour créer une intervention.' };
  return { visible: true, actif: true, raison: null };
}

/** Le lien de « Convertir en intervention » : le planning des interventions s'ouvre sur le volet préparé pour cette suite. */
export function lienConvertirIntervention(suite: { readonly id: string; readonly asset: { readonly id: string } }, natureId: string | null): string {
  const p = new URLSearchParams();
  p.set('depuisSuite', suite.id);
  p.set('machine', suite.asset.id);
  if (natureId !== null) p.set('nature', natureId);
  return `/planning/interventions?${p.toString()}`;
}

/** Ce que la suite dit de l'intervention qui la traite : « Intervention BI-… prévue » ou « soldée avec l'intervention BI-… ». */
export function phraseInterventionDeLaSuite(treatedBy: { readonly number: string; readonly status: string }): string {
  if (treatedBy.status === 'REVIEWED') return `Soldée avec l'intervention ${treatedBy.number}`;
  return `Intervention ${treatedBy.number} prévue`;
}

/** Le motif d'une annulation : rogné, trois caractères au moins (le serveur le contrôle aussi) ; `null` s'il est insuffisant. */
export function motifValide(saisie: string): string | null {
  const motif = saisie.trim();
  return motif.length >= 3 ? motif : null;
}

// --- La tournée d'une intervention (lot U1d) --------------------------------------------------------------------

/** L'état d'une occurrence de tournée, en français : une occurrence est faite quand toutes ses interventions sont validées. */
export const LIBELLE_ETAT_OCCURRENCE: Readonly<Record<'OPEN' | 'DONE' | 'CANCELLED', string>> = {
  OPEN: 'À faire',
  DONE: 'Faite',
  CANCELLED: 'Annulée',
};

/** « occurrence 2 sur 4 » : lu de la tournée, jamais recompté. */
export function phraseOccurrenceSurTotal(no: number, total: number): string {
  return `occurrence ${no} sur ${total}`;
}

/**
 * Combien d'interventions l'arrêt annulera d'après l'écran : celles des occurrences ouvertes d'aujourd'hui ou plus tard (ou sans
 * jour). C'est une estimation : le serveur reste juge et n'annule jamais une intervention déjà commencée ni passée.
 */
export function interventionsQuAnnuleraLArret(
  occurrences: readonly { readonly status: string; readonly plannedDate: string | null; readonly interventions: number }[],
  aujourdhui: string,
): number {
  return occurrences
    .filter((o) => o.status === 'OPEN' && (o.plannedDate === null || o.plannedDate >= aujourdhui))
    .reduce((somme, o) => somme + o.interventions, 0);
}

/** Ce que dit l'arrêt avant de le faire : combien d'interventions s'annulent au plus, et ce qui ne bouge pas. */
export function phraseArretIntervention(
  t: { readonly occurrences: readonly { readonly status: string; readonly plannedDate: string | null; readonly interventions: number }[] },
  aujourdhui: string,
): string {
  const n = interventionsQuAnnuleraLArret(t.occurrences, aujourdhui);
  const quoi =
    n === 0
      ? 'Aucune intervention à venir : rien ne s’annulera, la tournée sera simplement arrêtée.'
      : `${n === 1 ? '1 intervention à venir s’annulera' : `${n} interventions à venir s’annuleront`} (celles d’aujourd’hui ou plus tard, « à affecter » ou « planifiées »).`;
  return `${quoi} Les interventions déjà commencées, à contrôler ou validées, et les interventions passées ne bougent pas. L’arrêt est définitif.`;
}

/** Le bilan d'un arrêt de tournée. */
export function phraseResultatArret(annulees: number): string {
  return annulees === 0 ? 'Tournée arrêtée.' : `Tournée arrêtée : ${annulees === 1 ? '1 intervention annulée' : `${annulees} interventions annulées`}.`;
}

// --- Les heures d'un technicien (lot U2) -----------------------------------------------------------------------

/** Les heures se saisissent tant que l'intervention n'est ni validée ni annulée (y compris « à contrôler » : le bureau corrige). */
export function peutSaisirDesHeures(i: Pick<InterventionPlanning, 'status'>, peutGerer: boolean): boolean {
  return peutGerer && ['TO_ASSIGN', 'PLANNED', 'DRAFT', 'IN_PROGRESS', 'TO_REVIEW'].includes(i.status);
}

/**
 * Lit des heures saisies à la française (« 3 », « 7,5 », « 1h30 », « 45 min », « 10 min ») : vide = effacer (`null`), illisible ou
 * hors de 1 minute à 24 heures = `'ERREUR'`. Des heures SAISIES peuvent être courtes (un trajet de dix minutes se facture quinze).
 */
export function lireHeuresSaisies(texte: string): number | null | 'ERREUR' {
  return lireDuree(texte, 1);
}

/** On ne saisit pas d'heures pour un jour à venir. */
export function jourAvecHeuresPossibles(jour: string, aujourdhui: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(jour) && jour <= aujourdhui;
}

/** Le jour proposé d'emblée pour saisir des heures : le dernier jour travaillé qui n'est pas à venir, sinon aujourd'hui. */
export function jourPropose(jours: readonly { readonly day: string }[], aujourdhui: string): string {
  const passes = jours.map((j) => j.day).filter((d) => d <= aujourdhui);
  return passes.length > 0 ? (passes[passes.length - 1] as string) : aujourdhui;
}

/** « prévu 3 h · saisi 3 h 10 · facturé 3 h 15 » : ce qu'on sait des heures d'un technicien un jour donné (rien si rien). */
export function phraseHeuresMembre(m: { readonly minutesPlanned: number | null; readonly minutesEntered: number | null; readonly minutesBilled: number | null }): string {
  const morceaux: string[] = [];
  if (m.minutesPlanned !== null) morceaux.push(`prévu ${dureeFr(m.minutesPlanned)}`);
  if (m.minutesEntered !== null) morceaux.push(`saisi ${dureeFr(m.minutesEntered)}`);
  if (m.minutesBilled !== null) morceaux.push(`facturé ${dureeFr(m.minutesBilled)}`);
  return morceaux.join(' · ');
}

/** Ce que dit l'écran après la saisie : qui, quel jour, combien — ou « effacées ». */
export function phraseHeuresEnregistrees(nom: string, jour: string, minutes: number | null): string {
  return minutes === null ? `Heures de ${nom} effacées pour le ${jourFr(jour)}.` : `Heures de ${nom} enregistrées pour le ${jourFr(jour)} : ${dureeFr(minutes)}.`;
}

// --- Corriger les heures après la validation (lot U2b) --------------------------------------------------------

/** Les états où l'intervention est figée : plus de saisie sur l'équipe, seules les corrections d'heures restent possibles. */
const ETATS_FIGES = ['REVIEWED', 'TRANSMITTED', 'CLASSIFIED', 'TO_QUOTE', 'TO_INVOICE', 'CLOSED'];

/** Une intervention validée a ses heures corrigées par des lignes de plus ; ses corrections se lisent, celui qui valide les écrit. */
export function interventionFigee(i: Pick<InterventionPlanning, 'status'>): boolean {
  return ETATS_FIGES.includes(i.status);
}

/** Corriger les heures d'une intervention validée exige le droit de VALIDER (celui qui fige les heures est celui qui les corrige). */
export function peutCorrigerLesHeures(i: Pick<InterventionPlanning, 'status'>, peutValider: boolean): boolean {
  return peutValider && interventionFigee(i);
}

/** La raison d'une correction : trois caractères au moins, cinq cents au plus ; `null` si elle ne convient pas. */
export function lireRaisonCorrection(texte: string): string | null {
  const t = texte.trim();
  return t.length >= 3 && t.length <= 500 ? t : null;
}

/** « Gregory BONFILS, le 05/10/2026 : 1 h 35 retenues (facturé 1 h 45) au lieu de 1 h 00 (facturé 1 h 00) » : une correction, en clair. */
export function phraseCorrection(a: Pick<AjustementHeures, 'day' | 'technicianName' | 'minutesBefore' | 'minutesAfter' | 'billedBefore' | 'billedAfter'>): string {
  const apres = `${dureeFr(a.minutesAfter)} retenues (facturé ${dureeFr(a.billedAfter)})`;
  const avant =
    a.minutesBefore === null
      ? 'aucune heure retenue avant'
      : `${dureeFr(a.minutesBefore)} avant${a.billedBefore === null ? '' : ` (facturé ${dureeFr(a.billedBefore)})`}`;
  return `${a.technicianName}, le ${jourFr(a.day)} : ${apres} — ${avant}`;
}

/** Ce que dit l'écran après une correction. */
export function phraseCorrectionEnregistree(nom: string, jour: string, minutes: number): string {
  return `Heures de ${nom} corrigées pour le ${jourFr(jour)} : ${dureeFr(minutes)} retenues. L'ancienne valeur reste lisible dans la liste des corrections.`;
}
